import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import Page from '../../components/layout/Page'
import TimesheetReviewPage from '../timesheet-review'
import Icon from '../../components/AppIcon'
import PersonalHours from '../../components/PersonalHours'
import TimesheetClockCard from '../../components/TimesheetClockCard'
import { getPortalWorkspaces } from '../../lib/portalNav'
import { useRole } from '../../lib/roles'
import { hasPersonalTimesheet } from '../../lib/portalAccess.mjs'

const DETAILS = {
  exchange_desk: { eyebrow: 'Precious metals', description: 'Appraise gold and silver, build a quote, and complete a purchase.', action: 'Open exchange desk', number: '01' },
  product_listing: { eyebrow: 'Product catalog', description: 'Review product details, manage photos, and publish listings.', action: 'Open product listing', number: '02' },
  jobs: { eyebrow: 'Planning', description: 'A dedicated space for job tracking and scheduling. Coming soon.', action: 'View workspace', number: '03' },
}
const ORDER = ['exchange_desk', 'product_listing', 'jobs']

export function HomePageView({ role, attendanceEmployee = false, now = new Date() }) {
  const [shiftState, setShiftState] = useState('unknown')
  const activeShift = shiftState === 'working' || shiftState === 'on_break'
  const simplified = (role === 'staff' || !role) && !activeShift
  const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }).format(now))
  const greeting = hour < 12 ? 'Good morning.' : hour < 18 ? 'Good afternoon.' : 'Good evening.'
  const date = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' }).format(now)
  const workspaces = getPortalWorkspaces(role).sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id))
  if (role === 'accountant') return <Navigate to="/artgold-admin" replace />
  if (role === 'manager' || role === 'admin') return <TimesheetReviewPage />
  return <Page size="full" className={simplified ? '!pt-6' : '!pt-9 lg:!pt-14'} header={!simplified &&
    <div className="mb-10 lg:mb-12">
      <p className="portal-section-label mb-4">{date}</p>
      <h1 aria-label="Home" className="text-[38px] font-semibold leading-[1.1] tracking-[-0.055em] text-foreground sm:text-[48px] lg:text-[54px]">{greeting}</h1>
      <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">{role === 'staff' ? 'Your jobs and work time, in one place.' : 'Manage purchases, products, and daily operations.'}</p>
    </div>
  }>
    {!simplified && <><div className="mb-5 flex items-center justify-between border-b border-border pb-3">
      <h2 className="text-[13px] font-semibold tracking-tight text-foreground">Your workspaces</h2>
      <span className="portal-section-label">ArtGold operations</span>
    </div>
    <div className={'grid grid-cols-1 gap-4 ' + (workspaces.length === 1 ? 'max-w-xl' : 'sm:grid-cols-2 xl:grid-cols-[1.25fr_1fr_1fr]')}>
      {workspaces.map(item => {
        const detail = DETAILS[item.id]
        const featured = item.id === 'exchange_desk'
        return <Link key={item.id} to={item.path} className={'portal-workspace-card group flex min-h-[264px] min-w-0 flex-col rounded-xl border p-6 lg:min-h-[310px] lg:p-7 ' +
          (featured ? 'border-[#17191D] bg-[#17191D] text-white sm:col-span-2 xl:col-span-1' : 'border-border bg-card text-foreground hover:border-muted-foreground/40')}>
          <div className="mb-10 flex items-start justify-between gap-3">
            <span className={'text-[10px] font-medium uppercase tracking-[0.12em] ' + (featured ? 'text-white/60' : 'text-muted-foreground')}>{detail.number} / {detail.eyebrow}</span>
            <Icon name={item.icon} size={22} className={featured ? 'text-white/75' : 'text-muted-foreground'} />
          </div>
          <h3 className="text-[25px] font-medium leading-tight tracking-[-0.04em] lg:text-[28px]">{item.label}</h3>
          <p className={'mb-8 mt-3 max-w-[270px] text-[13px] leading-relaxed ' + (featured ? 'text-white/65' : 'text-muted-foreground')}>{detail.description}</p>
          <div className={'mt-auto flex items-center justify-between gap-2 border-t pt-4 text-xs font-medium ' + (featured ? 'border-white/15' : 'border-border')}>
            <span>{detail.action}</span><Icon name="ArrowUpRight" size={17} className={featured ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'} />
          </div>
        </Link>
      })}
    </div></>}
    <>{hasPersonalTimesheet(role, attendanceEmployee) && <><TimesheetClockCard key="employee-timesheet" className={simplified ? '' : 'mt-6'} onShiftStateChange={setShiftState} />
    <PersonalHours shiftState={shiftState} /></>}</>
    {!simplified && <div className="mt-9 flex flex-col gap-2 text-[11px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <p className="inline-flex items-center gap-1.5"><Icon name="ShieldCheck" size={13} />Access is managed by your administrator.</p>
    </div>}
  </Page>
}

export default function HomePage(props) {
  const { role, attendanceEmployee, loading } = useRole()
  return <HomePageView {...props} role={loading ? null : role} attendanceEmployee={!loading && attendanceEmployee} />
}
